import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useLang } from "@/context/LanguageContext";
import { listCategories, listApps } from "@/lib/api";
import AppCard from "@/components/AppCard";
import AdSlot from "@/components/AdSlot";
import { categoryColor } from "@/constants/categoryColors";

export const CategoriesIndexPage = () => {
  const { t } = useLang();
  const [cats, setCats] = useState([]);

  useEffect(() => {
    listCategories().then(setCats).catch(() => setCats([]));
  }, []);

  return (
    <main className="hac-container py-12 md:py-16">
      <div className="overline mb-3 text-[#2563EB]">{t.nav.categories}</div>
      <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-tight mb-3">
        {t.sections.categories}
      </h1>
      <p className="text-[#64748B] max-w-2xl mb-12">{t.sections.categoriesSub}</p>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {cats.map((c) => {
          const color = categoryColor(c.key);
          return (
            <Link
              key={c.key}
              to={`/categories/${c.key}`}
              className="hac-card p-6 md:p-8 block group relative overflow-hidden"
              data-testid={`category-${c.key}`}
              style={{ borderTop: `4px solid ${color}` }}
            >
              <div
                className="overline mb-2 inline-flex items-center gap-2"
                style={{ color }}
              >
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
                {c.app_count} apps
              </div>
              <h3 className="font-heading text-2xl md:text-3xl text-[#0F172A] mb-3">
                {t.categories[c.key]}
              </h3>
              <div className="flex flex-wrap gap-1 mb-5">
                {(c.features || []).slice(0, 5).map((f) => (
                  <span key={f} className="hac-chip text-[10px]">
                    {t.features[f] || f}
                  </span>
                ))}
                {c.features.length > 5 && (
                  <span className="hac-chip text-[10px]">+{c.features.length - 5}</span>
                )}
              </div>
              <div
                className="text-xs inline-flex items-center gap-1 font-medium"
                style={{ color }}
              >
                {t.common.overview} <ArrowRight size={12} />
              </div>
            </Link>
          );
        })}
      </div>
    </main>
  );
};

export const CategoryDetailPage = () => {
  const { key } = useParams();
  const { t } = useLang();
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    listApps({ category: key, sort: "score" })
      .then(setApps)
      .finally(() => setLoading(false));
  }, [key]);

  const title = t.categories[key] || key;
  const color = categoryColor(key);

  return (
    <main>
      <section
        className="border-b border-[#E2E8F0]"
        style={{
          background: `linear-gradient(180deg, ${color}12 0%, transparent 100%)`,
        }}
      >
        <div className="hac-container py-12 md:py-16">
          <Link
            to="/categories"
            className="text-xs text-[#64748B] hover:text-[#2563EB] inline-flex items-center gap-1 mb-6"
          >
            ← {t.nav.categories}
          </Link>
          <div className="overline mb-3 inline-flex items-center gap-2" style={{ color }}>
            <span className="w-2 h-2 rounded-full" style={{ backgroundColor: color }} />
            {t.nav.categories}
          </div>
          <h1 className="font-heading text-4xl md:text-6xl text-[#0F172A] leading-tight mb-3">
            {title}
          </h1>
          <p className="text-[#64748B]">
            {apps.length} {t.common.ofApps}
          </p>
        </div>
      </section>

      <AdSlot placement="category-banner" />

      <div className="hac-container py-12">
        {loading ? (
          <div className="text-center py-20 text-[#94A3B8]">…</div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {apps.map((a) => (
              <AppCard key={a.slug} app={a} />
            ))}
          </div>
        )}
      </div>
    </main>
  );
};
